import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/contexts/LangContext";
import PageMeta from "@/components/PageMeta";
import { localized } from "@/lib/projectI18n";
import {
  ArrowRight,
  ArrowUpRight,
  Layers,
  Boxes,
  Building2,
  TreePine,
  Cpu,
  Home as HomeIcon,
  Compass,
  Leaf,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMAGE = "/images/aqualina-cover-villa-pool.jpg";

const SERVICES = {
  de: [
    { icon: Compass, num: "01", title: "Projektentwicklung", text: "Standortanalyse, Bauland-Akquise, Konzeptentwicklung und Wirtschaftlichkeitsprüfung für Wohn- und Mischnutzungen." },
    { icon: Building2, num: "02", title: "Bauträgerleistungen", text: "Ganzheitliche Bauträgerabwicklung — von der Planungsfreigabe über das Wohnungseigentum bis zur Übergabe." },
    { icon: Layers, num: "03", title: "Architektur & BIM-Planung", text: "Koordinierte Modellierung in LOD 300–400. Architektur, Tragwerk und Haustechnik in einer Datenbasis." },
    { icon: HomeIcon, num: "04", title: "Villa-Projekte", text: "Individuelle Villen und Premium-Stadthäuser in ausgewählten Lagen — durchdacht, ruhig, langlebig." },
    { icon: Boxes, num: "05", title: "Innenausbau", text: "Hochwertige Ausstattung, Maßmöbel, Designbäder und ausgewählte Materialien für ein zeitloses Wohngefühl." },
    { icon: Cpu, num: "06", title: "Smart Building", text: "Gebäudetechnik und Smart-Home-Steuerung in Zusammenarbeit mit unserem Haustechnik-Partner ECO Building Technik." },
  ],
  en: [
    { icon: Compass, num: "01", title: "Project development", text: "Site analysis, land acquisition, concept and feasibility studies for residential and mixed-use developments." },
    { icon: Building2, num: "02", title: "Developer services", text: "End-to-end developer execution — from planning approval and condominium structuring to handover." },
    { icon: Layers, num: "03", title: "Architecture & BIM", text: "Coordinated modelling in LOD 300–400. Architecture, structure and MEP on a single data foundation." },
    { icon: HomeIcon, num: "04", title: "Villa projects", text: "Individual villas and premium townhouses in selected locations — considered, calm, built to last." },
    { icon: Boxes, num: "05", title: "Interior fit-out", text: "High-grade fittings, bespoke joinery, designer bathrooms and curated materials for timeless interiors." },
    { icon: Cpu, num: "06", title: "Smart building", text: "Building services and smart-home control with our MEP partner ECO Building Technik." },
  ],
  ru: [
    { icon: Compass, num: "01", title: "Девелопмент", text: "Анализ участка, приобретение земли, концепция и экономика для жилой и смешанной застройки." },
    { icon: Building2, num: "02", title: "Услуги застройщика", text: "Полный цикл застройщика — от согласования проекта до передачи объектов и оформления." },
    { icon: Layers, num: "03", title: "Архитектура и BIM", text: "Координированное моделирование LOD 300–400. Архитектура, конструкции, ИОС в одной модели." },
    { icon: HomeIcon, num: "04", title: "Виллы", text: "Индивидуальные виллы и премиальные таунхаусы в избранных локациях — спокойно, надёжно, долговечно." },
    { icon: Boxes, num: "05", title: "Отделка", text: "Высококачественная отделка, мебель на заказ, дизайнерские ванные, тщательно подобранные материалы." },
    { icon: Cpu, num: "06", title: "Smart Building", text: "Инженерные системы и умный дом совместно с нашим партнёром ECO Building Technik." },
  ],
  uk: [
    { icon: Compass, num: "01", title: "Девелопмент", text: "Аналіз ділянки, придбання землі, концепція та економіка для житлової та змішаної забудови." },
    { icon: Building2, num: "02", title: "Послуги забудовника", text: "Повний цикл забудовника — від погодження проєкту до передачі об'єктів та оформлення." },
    { icon: Layers, num: "03", title: "Архітектура та BIM", text: "Координоване моделювання LOD 300–400. Архітектура, конструкції, інженерні мережі та системи в одній моделі." },
    { icon: HomeIcon, num: "04", title: "Вілли", text: "Індивідуальні вілли та преміальні таунхауси в обраних локаціях — спокійно, надійно, довговічно." },
    { icon: Boxes, num: "05", title: "Оздоблення", text: "Високоякісне оздоблення, меблі на замовлення, дизайнерські ванні кімнати, ретельно підібрані матеріали." },
    { icon: Cpu, num: "06", title: "Smart Building", text: "Інженерні системи та розумний будинок спільно з нашим партнером ECO Building Technik." },
  ],
};

const PRINCIPLES = {
  de: [
    { num: "I", title: "Substanz vor Show.", text: "Wir bauen für Bewohner und Eigentümer, nicht für Renderings. Material, Detail und Bauphysik bestimmen das Ergebnis." },
    { num: "II", title: "Standort entscheidet.", text: "Wir entwickeln dort, wo Lage, Nachbarschaft und Mobilität ein langfristig stabiles Umfeld bieten." },
    { num: "III", title: "Disziplinierte Planung.", text: "Klare Prozesse, koordinierte Modelle, präzise Ausführung. Risiken werden früh erkannt und gesteuert." },
    { num: "IV", title: "Verantwortung im Betrieb.", text: "Energie, Wartung und Lebenszykluskosten denken wir von Anfang an mit. Nachhaltigkeit ist kein Etikett." },
  ],
  en: [
    { num: "I", title: "Substance over show.", text: "We build for residents and owners, not for renderings. Material, detail and physics define the outcome." },
    { num: "II", title: "Location decides.", text: "We develop where location, neighbourhood and mobility provide a stable long-term setting." },
    { num: "III", title: "Disciplined planning.", text: "Clear processes, coordinated models, precise execution. Risks are identified and managed early." },
    { num: "IV", title: "Operational responsibility.", text: "Energy, maintenance and life-cycle costs are part of design from day one. Sustainability is not a label." },
  ],
  ru: [
    { num: "I", title: "Суть, а не шоу.", text: "Мы строим для жильцов и собственников, а не для рендеров. Материал, деталь и физика — основа результата." },
    { num: "II", title: "Локация решает.", text: "Мы развиваем там, где местоположение, район и мобильность дают долгосрочную устойчивость." },
    { num: "III", title: "Дисциплинированное планирование.", text: "Чёткие процессы, координированные модели, точное исполнение. Риски выявляются и управляются заблаговременно." },
    { num: "IV", title: "Ответственность в эксплуатации.", text: "Энергия, обслуживание и жизненный цикл — часть проекта с самого начала." },
  ],
  uk: [
    { num: "I", title: "Суть, а не шоу.", text: "Ми будуємо для мешканців і власників, а не для рендерів. Матеріал, деталі та фізика — основа результату." },
    { num: "II", title: "Локація вирішує.", text: "Ми розвиваємо там, де місцезнаходження, район та мобільність забезпечують довгострокову стійкість." },
    { num: "III", title: "Дисципліноване планування.", text: "Чіткі процеси, координовані моделі, точне виконання. Ризики виявляються та управляються завчасно." },
    { num: "IV", title: "Відповідальність в експлуатації.", text: "Енергія, обслуговування та життєвий цикл — частина проєкту від самого початку." },
  ],
};

const SUSTAINABILITY = {
  de: { eyebrow: "Nachhaltigkeit", title: "Bauen, das Bestand hat.", body: "Energieeffiziente Gebäudehülle, sorgfältige Materialwahl und regenerative Energie wo sinnvoll — für Architektur, die nicht nur modern wirkt, sondern Bestand hat. Ökologisches Bauen entsteht bei uns durch präzise BIM-Planung und industrielle Produktion: koordinierte Modelle, kurze Bauzeiten, weniger Abfall auf der Baustelle. Wir orientieren unsere Projekte an europäischen Effizienz- und Lebenszyklus-Standards." },
  en: { eyebrow: "Sustainability", title: "Building that lasts.", body: "Energy-efficient envelopes, careful material selection and renewable energy where it makes sense — architecture that isn't just modern, but built to last. Ecological construction with us means precise BIM planning and industrial production: coordinated models, shorter build times, less waste on site. Our projects follow European efficiency and life-cycle standards." },
  ru: { eyebrow: "Устойчивость", title: "Строительство на десятилетия.", body: "Энергоэффективная оболочка, тщательный выбор материалов и возобновляемая энергия там, где это целесообразно — архитектура, которая не просто выглядит современной, а имеет фундамент на десятилетия. Экологичное строительство у нас — это точное BIM-проектирование и промышленное производство: координированные модели, короткие сроки строительства, меньше отходов на площадке. Мы следуем европейским стандартам эффективности и жизненного цикла." },
  uk: { eyebrow: "Стійкість", title: "Будівництво на десятиліття.", body: "Енергоефективна оболонка, ретельний вибір матеріалів та відновлювана енергія там, де це доцільно — архітектура, яка не просто виглядає сучасною, а має основу на десятиліття. Екологічне будівництво у нас — це точне BIM-проєктування та промислове виробництво: координовані моделі, короткі терміни будівництва, менше відходів на майданчику. Ми дотримуємося європейських стандартів ефективності та життєвого циклу." },
};

const HERO = {
  de: { eyebrow: "Royal House Bauträger GmbH · Wien", h1a: "Architektur,", h1b_em: "die Bestand", h1b: "hat.", lede: "Wir entwickeln, planen und realisieren Wohnimmobilien in ausgewählten europäischen Lagen — mit einer Haltung, die auf Substanz, Standort und langfristigem Wert basiert.", cta1: "Projekte ansehen", cta2: "Beratung anfragen" },
  en: { eyebrow: "Royal House Bauträger GmbH · Vienna", h1a: "Architecture,", h1b_em: "built to", h1b: "last.", lede: "We develop, plan and build residential properties in selected European locations — with a position rooted in substance, place and long-term value.", cta1: "View projects", cta2: "Request consultation" },
  ru: { eyebrow: "Royal House Bauträger GmbH · Вена", h1a: "Архитектура,", h1b_em: "которая остаётся", h1b: "надолго.", lede: "Мы разрабатываем, проектируем и реализуем жилую недвижимость в избранных европейских локациях — с опорой на качество, место и долгосрочную ценность.", cta1: "Смотреть проекты", cta2: "Заказать консультацию" },
  uk: { eyebrow: "Royal House Bauträger GmbH · Відень", h1a: "Архітектура,", h1b_em: "яка залишається", h1b: "надовго.", lede: "Ми розробляємо, проєктуємо та реалізуємо житлову нерухомість в обраних європейських локаціях — з опорою на якість, місце та довгострокову цінність.", cta1: "Переглянути проєкти", cta2: "Замовити консультацію" },
};

const INTRO = {
  de: { eyebrow: "Unternehmen", title: "Eine Bauträger- und Projektentwicklungsgruppe.", body: "Royal House Bauträger GmbH mit Sitz in Wien entwickelt Wohnprojekte im gehobenen Segment. Wir verbinden klassische Bauträgerexpertise mit moderner BIM-Planung, präziser Vorfertigung und einem internationalen Partnernetzwerk.", body2: "Unsere Projekte entstehen entlang vier Linien: Projektentwicklung, Bauträgerleistungen, Villa-Projekte und Innenausbau. Die technische Gebäudeausrüstung verantwortet unser Haustechnik-Partner ECO Building Technik." },
  en: { eyebrow: "Company", title: "A developer and project-development group.", body: "Royal House Bauträger GmbH, based in Vienna, develops residential projects in the upper segment. We combine classic developer expertise with modern BIM planning, precise prefabrication and an international partner network.", body2: "Our work runs along four lines: project development, developer services, villa projects and interior fit-out. Mechanical and electrical services are delivered with our MEP partner ECO Building Technik." },
  ru: { eyebrow: "Компания", title: "Группа застройщика и девелопмента.", body: "Royal House Bauträger GmbH со штаб-квартирой в Вене занимается жилыми проектами в верхнем сегменте. Мы сочетаем классический опыт застройщика с современным BIM-проектированием, точной префабрикацией и международной партнёрской сетью.", body2: "Наша работа ведётся по четырём направлениям: девелопмент, услуги застройщика, виллы, отделка. Инженерные системы реализует наш партнёр ECO Building Technik." },
  uk: { eyebrow: "Компанія", title: "Група забудовника та девелопменту.", body: "Royal House Bauträger GmbH зі штаб-квартирою у Відні займається житловими проєктами у верхньому сегменті. Ми поєднуємо класичний досвід забудовника з сучасним BIM-проєктуванням, точним префабрікацією та міжнародною партнерською мережею.", body2: "Наша робота ведеться за чотирма напрямками: девелопмент, послуги забудовника, вілли, оздоблення. Інженерні системи реалізує наш партнер ECO Building Technik." },
};

const PROJ_TITLE = { de: { eyebrow: "Projekte", t1: "Ausgewählte", t2: "Vorhaben.", cta: "Alle Projekte" }, en: { eyebrow: "Projects", t1: "Selected", t2: "developments.", cta: "All projects" }, ru: { eyebrow: "Проекты", t1: "Избранные", t2: "проекты.", cta: "Все проекты" }, uk: { eyebrow: "Проєкти", t1: "Обрані", t2: "проєкти.", cta: "Усі проєкти" } };
const PARTNER_TITLE = { de: { eyebrow: "Partner", t1: "Ein internationales", t2: "Partnernetzwerk." }, en: { eyebrow: "Partners", t1: "An international", t2: "partner network." }, ru: { eyebrow: "Партнёры", t1: "Международная", t2: "сеть партнёров." }, uk: { eyebrow: "Партнери", t1: "Міжнародна", t2: "мережа партнерів." } };
const CONTACT_TITLE = { de: { eyebrow: "Kontakt", t1: "Lassen Sie uns", t2: "sprechen.", body: "Wir beraten Sie persönlich zu laufenden Projekten und Entwicklungen.", btn: "Beratung anfragen" }, en: { eyebrow: "Contact", t1: "Let's", t2: "speak.", body: "We advise you personally on current projects and developments.", btn: "Request consultation" }, ru: { eyebrow: "Контакты", t1: "Поговорим", t2: "лично.", body: "Мы консультируем по текущим проектам и разработкам.", btn: "Заказать консультацию" }, uk: { eyebrow: "Контакти", t1: "Поговоримо", t2: "особисто.", body: "Ми консультуємо щодо поточних проєктів та розробок.", btn: "Замовити консультацію" } };

const PARTNERS_LIST = [
  { name: "ECO Building Technik", role: "Haustechnik · Smart Building", to: "/partner/building-technik" },
  { name: "Budova", role: "Industrielle Vorfertigung · BIM-ERP", to: "/partner/budova" },
  { name: "Well-Being ConTech", role: "Bautechnologie · Materialien", to: "/partner/wellbeing" },
  { name: "Ukraine Rebuilding Alliance", role: "Initiative · Wiederaufbau", to: "/partner/ukraine-rebuild" },
];

export default function HomePage() {
  const { t, lang } = useLang();
  const [projects, setProjects] = useState([]);
  const services = SERVICES[lang] || SERVICES.ru || SERVICES.de;
  const principles = PRINCIPLES[lang] || PRINCIPLES.ru || PRINCIPLES.de;
  const sus = SUSTAINABILITY[lang] || SUSTAINABILITY.ru || SUSTAINABILITY.de;
  const hero = HERO[lang] || HERO.ru || HERO.de;
  const intro = INTRO[lang] || INTRO.ru || INTRO.de;
  const projTitle = PROJ_TITLE[lang] || PROJ_TITLE.ru || PROJ_TITLE.de;
  const partnerTitle = PARTNER_TITLE[lang] || PARTNER_TITLE.ru || PARTNER_TITLE.de;
  const contactTitle = CONTACT_TITLE[lang] || CONTACT_TITLE.ru || CONTACT_TITLE.de;

  useEffect(() => {
    axios
      .get(`${API}/projects`)
      .then((res) => setProjects(res.data.slice(0, 4)))
      .catch((err) => console.warn("Home projects load failed:", err?.message));
    // API/axios are stable module-level references; run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="home-page">
      <PageMeta pageKey="home" />

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-end overflow-hidden bg-[#051630]">
        <img
          src={HERO_IMAGE}
          alt="Royal House Bauträger — Premium Architektur"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
          data-testid="hero-image"
        />
        {/* Premium navy overlay — 75% navy for stronger institutional contrast */}
        <div className="absolute inset-0" style={{ background: "rgba(5, 22, 48, 0.75)" }} />
        <div
          className="absolute inset-x-0 top-0 h-40 pointer-events-none"
          style={{ background: "linear-gradient(to bottom, rgba(5,22,48,0.55), rgba(5,22,48,0))" }}
        />
        <div
          className="absolute inset-x-0 bottom-0 h-48 pointer-events-none"
          style={{ background: "linear-gradient(to top, rgba(5,22,48,0.55), rgba(5,22,48,0))" }}
        />

        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 pb-24 md:pb-32 lg:pb-36 pt-32 w-full">
          <div className="max-w-5xl rh-fade-up">
            {/* Eyebrow */}
            <div className="flex items-center gap-4 mb-10 md:mb-14">
              <span className="w-12 h-px bg-white/70" />
              <span className="text-[11px] md:text-xs tracking-[0.32em] uppercase text-white font-medium">
                {hero.eyebrow}
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-display font-light text-[2.75rem] leading-[1.02] sm:text-6xl md:text-7xl lg:text-[7.5rem] xl:text-[8.5rem] tracking-[-0.02em] uppercase lg:leading-[0.92]">
              {hero.h1a}
              <br />
              <span className="italic font-extralight">{hero.h1b_em}</span> {hero.h1b}
            </h1>

            {/* Lede */}
            <p className="mt-10 md:mt-14 max-w-2xl text-base md:text-lg lg:text-xl text-white/85 leading-[1.6] font-light">
              {hero.lede}
            </p>

            {/* CTAs */}
            <div className="mt-12 md:mt-16 flex flex-col sm:flex-row gap-4 sm:gap-5">
              <Link
                to="/projekte"
                data-testid="hero-cta-projekte"
                className="group inline-flex items-center justify-between gap-6 bg-white text-[#051630] font-semibold tracking-[0.2em] uppercase text-[11px] md:text-xs px-8 md:px-10 py-5 md:py-6 border border-white hover:bg-transparent hover:text-white transition-colors duration-300"
              >
                <span>{hero.cta1}</span>
                <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/kontakt"
                data-testid="hero-cta-kontakt"
                className="group inline-flex items-center justify-between gap-6 bg-transparent text-white font-medium tracking-[0.2em] uppercase text-[11px] md:text-xs px-8 md:px-10 py-5 md:py-6 border border-white/45 hover:border-white hover:bg-white/5 transition-colors duration-300"
              >
                <span>{hero.cta2}</span>
                <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>

          {/* Footer line: scroll indicator + brand mark */}
          <div className="absolute left-6 md:left-12 lg:left-20 right-6 md:right-12 lg:right-20 bottom-6 md:bottom-8 hidden md:flex items-end justify-between text-[10px] tracking-[0.32em] uppercase text-white/55">
            <span className="flex items-center gap-3">
              <span className="block w-px h-10 bg-white/35 rh-pulse" />
              <span>{t("common.scroll_explore")}</span>
            </span>
            <span className="hidden lg:block">{t("home.eyebrow")}</span>
          </div>
        </div>
      </section>

      {/* UNTERNEHMEN */}
      <section className="py-24 md:py-32 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 grid md:grid-cols-12 gap-10 lg:gap-20">
        <div className="md:col-span-5 min-w-0">
          <p className="text-xs tracking-[0.3em] uppercase text-white/55 mb-6">01 · {intro.eyebrow}</p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] break-words hyphens-auto">
            {intro.title}
          </h2>
        </div>
        <div className="md:col-span-7 md:pt-12 space-y-6 min-w-0">
          <p className="text-base md:text-lg text-white/75 leading-relaxed font-light">{intro.body}</p>
          <p className="text-base md:text-lg text-white/75 leading-relaxed font-light">{intro.body2}</p>
          <Link to="/ueber-uns" className="rh-link mt-6 inline-flex items-center gap-3 text-sm tracking-[0.18em] uppercase font-medium pt-2" data-testid="home-link-ueber-uns">
            {t("home.intro.cta")} <ArrowUpRight size={16} strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* LEISTUNGEN — 6er Bento */}
      <section className="py-24 md:py-32 bg-[#0A2244]/60 border-y border-white/10">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
          <p className="text-xs tracking-[0.3em] uppercase text-white/55 mb-6">02 · Leistungen</p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] max-w-3xl mb-16">
            Vier Geschäftsfelder. <br />Ein präzises System.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
            {services.map((f) => (
              <div key={f.num} className="border-b border-r border-white/10 p-8 md:p-10 group hover:bg-white/[0.03] transition-colors" data-testid={`home-service-${f.num}`}>
                <div className="flex items-start justify-between mb-8">
                  <span className="font-display text-3xl text-white/30">{f.num}</span>
                  <f.icon size={28} strokeWidth={1.25} className="text-white/80 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight mb-4 leading-tight">{f.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VILLA & INNENAUSBAU SPLIT */}
      <section className="grid md:grid-cols-2 border-y border-white/10">
        <div className="relative aspect-[4/5] md:aspect-auto overflow-hidden border-b md:border-b-0 md:border-r border-white/10">
          <img src="/images/aqualina-02-pool-haus.jpg" alt="Villa-Projekte" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#051630] via-[#051630]/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 lg:p-16">
            <p className="text-[11px] tracking-[0.3em] uppercase text-white/65 mb-4">03 · Villa-Projekte</p>
            <h3 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05]">Villen mit Haltung.</h3>
            <p className="mt-5 max-w-md text-sm md:text-base text-white/85 leading-relaxed">Individuell entworfene Villen in ausgewählten Lagen. Klare Architektur, präzise Detaillierung, ruhige Materialität.</p>
            <Link to="/projekte" className="rh-link mt-6 inline-flex items-center gap-3 text-sm tracking-[0.18em] uppercase font-medium" data-testid="home-link-villa">
              {t("home.projects.cta")} <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
        <div className="relative aspect-[4/5] md:aspect-auto overflow-hidden">
          <img src="/images/aqualina-04-wohnzimmer.jpg" alt="Innenausbau" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#051630] via-[#051630]/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 lg:p-16">
            <p className="text-[11px] tracking-[0.3em] uppercase text-white/65 mb-4">04 · Innenausbau</p>
            <h3 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05]">Materialien, die bleiben.</h3>
            <p className="mt-5 max-w-md text-sm md:text-base text-white/85 leading-relaxed">Maßgefertigter Innenausbau, sorgfältig kuratierte Materialien, Designbäder und ausgesuchte Hölzer für ein zeitloses Wohngefühl.</p>
            <Link to="/leistungen" className="rh-link mt-6 inline-flex items-center gap-3 text-sm tracking-[0.18em] uppercase font-medium" data-testid="home-link-innenausbau">
              {t("home.e2e.cta")} <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* PRINZIPIEN */}
      <section className="py-24 md:py-32 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
        <p className="text-xs tracking-[0.3em] uppercase text-white/55 mb-6">05 · Haltung</p>
        <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] max-w-3xl mb-16">
          Vier Prinzipien, die unsere Arbeit prägen.
        </h2>
        <div className="border-t border-white/10">
          {principles.map((p, i) => (
            <div key={p.num} className="grid grid-cols-12 gap-6 md:gap-12 border-b border-white/10 py-8 md:py-12 group hover:bg-white/[0.02] transition-colors" data-testid={`home-principle-${i}`}>
              <div className="col-span-2 md:col-span-1 font-display text-2xl text-white/40 pt-1">{p.num}</div>
              <div className="col-span-10 md:col-span-4">
                <h3 className="font-display text-xl md:text-3xl font-light tracking-tight uppercase">{p.title}</h3>
              </div>
              <div className="col-span-12 md:col-span-7 text-white/75 text-sm md:text-base leading-relaxed">{p.text}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SMART BUILDING & NACHHALTIGKEIT */}
      <section className="bg-[#0A2244]/60 border-y border-white/10 py-24 md:py-32">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 grid md:grid-cols-12 gap-10 lg:gap-20">
          <div className="md:col-span-6">
            <p className="text-xs tracking-[0.3em] uppercase text-white/55 mb-6">06 · Smart Building</p>
            <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-6">
              Gebäudetechnik, ruhig integriert.
            </h2>
            <p className="text-base md:text-lg text-white/75 leading-relaxed font-light">
              Heizung, Kälte, Lüftung, Sanitär, Elektro und Gebäudeautomation realisieren wir gemeinsam mit unserem
              Haustechnik-Partner <strong className="text-white">ECO Building Technik</strong>. Werkseitig vorintegriert,
              modular getestet, auf der Baustelle präzise in Betrieb genommen.
            </p>
            <Link to="/partner/building-technik" className="rh-link mt-6 inline-flex items-center gap-3 text-sm tracking-[0.18em] uppercase font-medium" data-testid="home-link-buildingtechnik">
              Mehr zum Partner <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
          <div className="md:col-span-6">
            <p className="text-xs tracking-[0.3em] uppercase text-white/55 mb-6">07 · {sus.eyebrow}</p>
            <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-6 flex items-start gap-4">
              <TreePine size={36} strokeWidth={1} className="mt-1 shrink-0 opacity-70" />
              <span>{sus.title}</span>
            </h2>
            <p className="text-base md:text-lg text-white/75 leading-relaxed font-light">{sus.body}</p>
          </div>
        </div>
      </section>

      {/* PRODUKTION A+++ TEASER */}
      <section className="relative overflow-hidden border-b border-white/10" data-testid="home-produktion-teaser">
        <img
          src="/images/prod-hero.jpg"
          alt={t("home.prod.title")}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#051630] via-[#051630]/85 to-[#051630]/40" />
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-32">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <p className="text-xs tracking-[0.3em] uppercase text-white/60">{t("home.prod.eyebrow")}</p>
              <span className="inline-flex items-center gap-1.5 border border-emerald-300/40 bg-emerald-400/15 text-emerald-200 px-3 py-1 text-[11px] tracking-[0.15em] uppercase">
                <Leaf size={12} strokeWidth={1.5} /> A+++
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-light uppercase tracking-tight leading-[1.03]">
              {t("home.prod.title")}
            </h2>
            <p className="mt-8 text-base md:text-lg text-white/80 leading-relaxed font-light">{t("home.prod.body")}</p>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg border-t border-white/15 pt-8">
              <div>
                <p className="font-display text-2xl md:text-3xl font-light">72.000 m²</p>
                <p className="mt-1 text-[10px] tracking-[0.2em] uppercase text-white/50">{t("home.prod.fact1")}</p>
              </div>
              <div>
                <p className="font-display text-2xl md:text-3xl font-light">9,5</p>
                <p className="mt-1 text-[10px] tracking-[0.2em] uppercase text-white/50">{t("home.prod.fact2")}</p>
              </div>
              <div>
                <p className="font-display text-2xl md:text-3xl font-light">A+++</p>
                <p className="mt-1 text-[10px] tracking-[0.2em] uppercase text-white/50">{t("home.prod.fact3")}</p>
              </div>
            </div>

            <Link
              to="/produktion"
              data-testid="home-link-produktion"
              className="group mt-10 inline-flex items-center justify-between gap-6 bg-white text-[#051630] font-semibold tracking-[0.2em] uppercase text-[11px] md:text-xs px-8 md:px-10 py-5 border border-white hover:bg-transparent hover:text-white transition-colors duration-300"
            >
              <span>{t("home.prod.cta")}</span>
              <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* PROJEKTE */}
      <section className="py-24 md:py-32">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-white/55 mb-6">08 · {projTitle.eyebrow}</p>
              <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05]">
                {projTitle.t1} <br />{projTitle.t2}
              </h2>
            </div>
            <Link to="/projekte" className="rh-link self-start md:self-end text-sm tracking-[0.18em] uppercase font-medium inline-flex items-center gap-3" data-testid="home-link-projekte">
              {projTitle.cta} <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {projects.map((p) => (
              <Link key={p.id} to={`/projekte/${p.slug}`} data-testid={`home-project-${p.slug}`} className="group block border border-white/10 hover:border-white/40 transition-colors">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                </div>
                <div className="p-6 md:p-8 flex items-start justify-between gap-6">
                  <div>
                    <p className="text-[11px] tracking-[0.25em] uppercase text-white/55 mb-3">{p.location} · {p.year}</p>
                    <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight">{p.title}</h3>
                    <p className="mt-3 text-sm text-white/70 leading-relaxed line-clamp-2">{localized(p, "short_description", lang)}</p>
                  </div>
                  <div className="shrink-0 w-12 h-12 border border-white/20 flex items-center justify-center group-hover:bg-white group-hover:text-[#051630] group-hover:border-white transition-all">
                    <ArrowUpRight size={18} strokeWidth={1.5} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER */}
      <section className="bg-[#0A2244]/60 border-y border-white/10 py-24 md:py-32">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
          <p className="text-xs tracking-[0.3em] uppercase text-white/55 mb-6">09 · {partnerTitle.eyebrow}</p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] max-w-3xl mb-16">
            {partnerTitle.t1} <br />{partnerTitle.t2}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
            {PARTNERS_LIST.map((p, i) => (
              <Link key={p.name} to={p.to} data-testid={`home-partner-${i}`} className="border-b border-r border-white/10 p-8 md:p-10 group hover:bg-white/[0.04] transition-colors">
                <span className="font-display text-2xl text-white/30">0{i + 1}</span>
                <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight mt-6">{p.name}</h3>
                <p className="text-[11px] tracking-[0.25em] uppercase text-white/55 mt-3">{p.role}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-white/55 group-hover:text-white">
                  Partner ansehen <ArrowUpRight size={12} strokeWidth={1.5} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* KONTAKT */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 rh-grid-bg opacity-30" />
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="text-xs tracking-[0.3em] uppercase text-white/55 mb-6">10 · {contactTitle.eyebrow}</p>
            <h2 className="font-display text-4xl md:text-6xl font-light uppercase tracking-tight leading-[1.02]">
              {contactTitle.t1} <br /><span className="italic">{contactTitle.t2}</span>
            </h2>
            <p className="mt-6 max-w-2xl text-white/75 font-light text-lg">{contactTitle.body}</p>
          </div>
          <div className="md:col-span-4">
            <Link to="/kontakt" data-testid="home-cta-final" className="rh-btn-primary inline-flex items-center justify-center gap-3 bg-white text-[#051630] font-bold tracking-[0.18em] uppercase text-xs px-8 py-5 border border-white w-full md:w-auto">
              <span>{contactTitle.btn}</span>
              <ArrowRight size={16} strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
